import { useState, useEffect, useCallback } from 'react';
import { getSvgPath } from 'figma-squircle';

/**
 * Applies Figma's exact superellipse algorithm to any element via clip-path.
 * Uses a callback ref so it works even when the element mounts after the initial
 * render (e.g. after a loading state). ResizeObserver keeps the path in sync
 * if the element changes size.
 *
 * @param {object} options
 * @param {number} options.cornerRadius      - Corner radius in px (matches Figma)
 * @param {number} options.cornerSmoothing   - 0–1, where 1 = 100% Figma smoothing
 */
export function useSquircle({ cornerRadius = 20, cornerSmoothing = 1 } = {}) {
  // Store the DOM node in state so the effect re-runs when it mounts/unmounts
  const [element, setElement] = useState(null);
  const [clipPath, setClipPath] = useState('');

  // Callback ref — called with the DOM node as soon as it's in the tree
  const ref = useCallback((node) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;

    const update = (width, height) => {
      if (width <= 0 || height <= 0) return;
      const path = getSvgPath({
        width: Math.round(width),
        height: Math.round(height),
        cornerRadius,
        cornerSmoothing,
      });
      setClipPath(`path('${path}')`);
    };

    // Measure after the browser has committed layout for this frame
    const measure = () => update(element.offsetWidth, element.offsetHeight);
    const rafId = requestAnimationFrame(measure);

    // Re-measure on resize — defer to rAF so we read the settled layout size
    let pendingRaf = null;
    const observer = new ResizeObserver(() => {
      if (pendingRaf) cancelAnimationFrame(pendingRaf);
      pendingRaf = requestAnimationFrame(() => {
        update(element.offsetWidth, element.offsetHeight);
      });
    });
    observer.observe(element);

    return () => {
      cancelAnimationFrame(rafId);
      if (pendingRaf) cancelAnimationFrame(pendingRaf);
      observer.disconnect();
    };
  }, [element, cornerRadius, cornerSmoothing]);

  return {
    ref,
    squircleStyle: clipPath ? { clipPath } : {},
  };
}
